/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { MapsService } from './src/services/maps-service';
import { DeepScanManager } from './src/features/deep-scan/deep-scan-manager';
import { ChatState } from './src/components/chat-view';
import { campaignService, Campaign, CampaignStep } from './src/services/campaign-service';
import { leadService } from './src/services/lead-service';
import { evolutionService } from './src/services/evolution-service';
import { getTargetKeywords } from './src/services/target-data';
export { ChatState };

// Import components to ensure they are registered
import './src/components/chat-view';
import './src/components/leads-list';
import './src/components/lead-drawer';

enum ChatTab {
  GEMINI,
  LEADS,
}

@customElement('gdm-playground')
export class Playground extends LitElement {
  @state() chatState = ChatState.IDLE;
  @state() selectedChatTab = ChatTab.GEMINI;
  @state() messages: HTMLElement[] = [];
  @state() savedLeads: any[] = [];
  
  @state() selectedPlace: any = null;
  @state() isDrawerOpen: boolean = false;
  
  @state() isScanning: boolean = false;
  @state() scanProgress: number = 0;
  @state() scanStatus: string = '';

  @state() isEnriching: boolean = false;
  @state() enrichedCount: number = 0;
  @state() isWhatsAppEnriching: boolean = false;
  @state() whatsappEnrichedCount: number = 0;
  @state() lastWhatsAppError: string | null = null;
  @state() private activeCampaign: Campaign | null = null;
  @state() private currentStep: CampaignStep | null = null;
  @state() private currentModeIndex: number = 0;
  @state() private isSelectionMode: boolean = false;
  @state() private manualBounds: any = null;
  @state() private manualQuery: string = '';
  private enrichmentQueue: any[] = [];
  private whatsappQueue: any[] = [];
  private enrichmentTotal = 0;
  private whatsappTotal = 0;
  private isProcessingEnrichment = false;
  private isProcessingWhatsApp = false;

  private mapsService = MapsService.getInstance();
  private deepScanManager: DeepScanManager;
  private mapContainer = document.createElement('div');

  sendMessageHandler?: (input: string, role: string) => Promise<void>;

  constructor() {
    super();
    this.mapContainer.className = 'map-container';
    this.mapContainer.style.width = '100%';
    this.mapContainer.style.height = '100%';
    
    this.deepScanManager = new DeepScanManager((update) => {
      this.scanProgress = update.progress;
      this.scanStatus = update.status;
      
      const wasScanning = this.isScanning;
      this.isScanning = update.isScanning;
      
      if (update.newLeadsCount && update.newLeadsCount > 0) {
        // Persistir novos leads no banco global (para deduplicação futura)
        const lastLeads = update.leads.slice(-update.newLeadsCount).map(l => ({
          ...l,
          campaignId: this.activeCampaign?.id
        }));
        
        // Dispara o salvamento e deixa os eventos de persistência cuidarem da atualização da UI
        leadService.saveLeads(lastLeads);
        
        // Enriquecimento em Paralelo
        if (this.activeCampaign?.autoEnrich || this.isEnriching) {
          this.enrichmentQueue.push(...lastLeads);
          if (!this.isProcessingEnrichment) {
            this.isProcessingEnrichment = true;
            this.isEnriching = true;
            this.processEnrichmentQueue();
          }
        }
      }
      
      // Se a varredura terminou AGORA, damos um refresh final nos marcadores para garantir que tudo está sincronizado
      if (wasScanning && !update.isScanning) {
        this.refreshMarkers();
        
        // Campanha: Próximo passo se necessário
        if (this.activeCampaign) {
          this.handleCampaignStepComplete(update.newLeadsCount || 0);
        }
      }
    });

    this.addEventListener('start-enrichment', (e: any) => {
      console.log('Enrichment event received in Playground');
      this.startEnrichment();
    });

    window.addEventListener('campaign-updated', () => {
      this.syncCampaign();
      this.refreshMarkers();
    });
    window.addEventListener('leads-persisted', () => {
      this.savedLeads = leadService.getLeads();
      
      const leadsToNotify = this.getLeadsForCurrentContext();
      
      // Otimização Crítica: Se estivermos em meio a uma varredura, não limpamos o mapa. 
      if (!this.isScanning) {
        this.refreshMarkers();
      }
      
      this.dispatchEvent(new CustomEvent('leads-updated', { detail: leadsToNotify }));
    });

    this.init();
    this.refreshMarkers();
    
    // Listen for marker clicks from DeepScanManager
    window.addEventListener('lead-marker-clicked', (e: any) => {
      this.openLeadDetails(e.detail);
    });

    window.addEventListener('enrich-whatsapp', async (e: any) => {
      const lead = e.detail;
      try {
        const whatsappData = await evolutionService.fetchWhatsAppProfile(lead.formatted_phone_number);
        const updatedLead = { ...lead, whatsappData };
        
        // Update local state
        const index = this.savedLeads.findIndex(l => l.place_id === lead.place_id);
        if (index !== -1) {
          this.savedLeads[index] = updatedLead;
          this.savedLeads = [...this.savedLeads];
          if (this.selectedPlace?.place_id === lead.place_id) {
             this.selectedPlace = updatedLead;
          }
        }
        
        // Persist
        await leadService.updateLead(updatedLead);
        this.dispatchEvent(new CustomEvent('leads-updated', { detail: this.savedLeads }));
      } catch (err: any) {
        console.error('WhatsApp Enrichment error:', err);
        // Dispatch an event to clear loading state in case of error
        window.dispatchEvent(new CustomEvent('enrich-whatsapp-error', { detail: { place_id: lead.place_id, error: err.message } }));
      } finally {
        window.dispatchEvent(new CustomEvent('enrich-whatsapp-complete', { detail: { place_id: lead.place_id } }));
      }
    });

    this.addEventListener('request-lead-details', (e: any) => {
      this.openLeadDetails(e.detail);
    });

    this.addEventListener('start-whatsapp-batch', () => {
      this.startWhatsAppBatch();
    });

    this.addEventListener('delete-leads', async (e: any) => {
      const placeIdsToDelete = e.detail;
      await leadService.deleteMultipleLeads(placeIdsToDelete);
      this.savedLeads = leadService.getLeads();
      this.dispatchEvent(new CustomEvent('leads-updated', { detail: this.savedLeads }));
    });
  }

  private toggleSelectionMode() {
    this.isSelectionMode = !this.isSelectionMode;
    if (this.isSelectionMode) {
      this.manualBounds = null;
      this.mapsService.clearSelection();
      this.mapsService.startRectangleSelection((bounds) => {
        this.manualBounds = bounds;
        this.isSelectionMode = false;
      });
    } else {
      this.mapsService.stopRectangleSelection();
    }
  }

  private clearManualSelection() {
    this.manualBounds = null;
    this.mapsService.clearSelection();
  }

  private async scanManualArea() {
    if (!this.manualBounds || !this.manualQuery.trim()) return;
    
    this.selectedChatTab = ChatTab.LEADS;
    
    await this.deepScanManager.performScan(
      this.manualQuery, 
      "Área Personalizada",
      "standard",
      this.savedLeads,
      undefined,
      this.manualBounds
    );
    
    this.manualBounds = null;
    this.manualQuery = "";
    this.mapsService.clearSelection();
  }

  public getLeadsForCurrentContext() {
    if (this.activeCampaign && this.activeCampaign.status !== 'completed') {
      const activeId = String(this.activeCampaign.id);
      return this.savedLeads.filter(l => l.campaignId && String(l.campaignId) === activeId);
    }
    // No modo manual (Assistente), mostramos apenas leads sem campanha vinculada
    return this.savedLeads.filter(l => !l.campaignId);
  }

  public refresh() {
    this.refreshMarkers();
    // Notificar filhos sobre a lista filtrada
    this.dispatchEvent(new CustomEvent('leads-updated', { detail: this.getLeadsForCurrentContext() }));
  }

  private refreshMarkers() {
    if (!this.mapsService.map) return;
    
    // Otimização: Se estivermos escaneando, NÃO limpamos o mapa para evitar flicker.
    if (this.isScanning) {
      console.log('Skipping refreshMarkers while scanning.');
      return;
    }

    this.mapsService.clearAllMarkers();
    
    const leadsToDraw = this.getLeadsForCurrentContext();
    
    console.log(`Refreshing markers: drawing ${leadsToDraw.length} leads (Active Campaign: ${this.activeCampaign?.id || 'None'})`);

    leadsToDraw.forEach(lead => {
      // Handles both direct object and function-style (Google Maps native) coordinates
      const loc = lead.geometry?.location;
      if (!loc) return;

      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      
      if (lat !== undefined && lng !== undefined) {
        const marker = this.mapsService.addMarker({
          position: { lat, lng },
          title: lead.name,
          icon: lead.campaignId ? undefined : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
        });
        marker.addListener('click', () => this.openLeadDetails(lead));
      }
    });
  }

  private syncCampaign() {
    const prevCampaignId = this.activeCampaign?.id;
    const prevStepIndex = this.activeCampaign?.currentStepIndex;
    
    this.activeCampaign = campaignService.getActiveCampaign() || null;
    const res = campaignService.getCurrentStep();
    this.currentStep = res ? res.step : null;

    if (this.activeCampaign?.id !== prevCampaignId || this.activeCampaign?.currentStepIndex !== prevStepIndex) {
      this.currentModeIndex = 0;
    }
  }

  private async handleCampaignStepComplete(newLeads: number, isSkip = false) {
    if (!this.activeCampaign) return;
    
    const modes = this.activeCampaign.scanModes || ['standard'];
    
    // Se não for skip e ainda tem mais modos para este passo
    if (!isSkip && this.currentModeIndex < modes.length - 1) {
      this.currentModeIndex++;
      this.scanStatus = `Avançando para modo: ${modes[this.currentModeIndex]}`;
      
      // Esperar o delay configurado antes do próximo ciclo de modo
      const delay = this.activeCampaign.stepDelay || 2000;
      await new Promise(r => setTimeout(r, delay));
      
      this.runNextCampaignStep();
      return;
    }

    // Se acabou os modos deste passo ou é um skip inteligente
    if (isSkip) {
      await campaignService.skipStep();
    } else {
      await campaignService.completeStep(newLeads);
    }
    
    this.syncCampaign();

    // Auto-enriquecimento final (caso tenha sobrado algo ou não tenha autoEnrich ativado mas terminou agora)
    if (this.activeCampaign?.autoEnrich && newLeads > 0) {
      this.startEnrichment();
    }

    // Se a campanha ainda está ativa, vamos para o próximo passo/alvo automaticamente
    if (this.activeCampaign?.status === 'active') {
      const nextStep = campaignService.getCurrentStep();
      if (nextStep) {
        this.scanStatus = `Aguardando próximo alvo...`;
        // Transição rápida para o próximo alvo
        setTimeout(() => {
          if (this.activeCampaign?.status === 'active') { // Double check before auto-running
            this.runNextCampaignStep();
          }
        }, 800);
      } else {
        this.scanStatus = `Campanha Finalizada com Sucesso!`;
      }
    }
  }

  private async runNextCampaignStep() {
    if (!this.currentStep || !this.activeCampaign) return;
    
    // Inteligência Global de Tarefas: Verificar se esta combinação Alvo + Região já foi concluída
    // O checkStepExists olha em todas as campanhas por status 'completed' ou 'processing'
    const isRedundant = campaignService.checkStepExists(this.currentStep.targetId, this.currentStep.regionId);
    
    if (isRedundant) {
      this.scanStatus = `✨ Inteligência Nyroh: Pulando "${this.currentStep.targetName}" em ${this.currentStep.regionName} (Tarefa já realizada anteriormente)`;
      this.isScanning = false;
      this.requestUpdate();
      
      // Delay otimizado para o usuário perceber a inteligência agindo
      await new Promise(r => setTimeout(r, 600));
      
      this.handleCampaignStepComplete(0, true); // Completa como 'skipped'
      return;
    }

    // Garantir que a campanha está com status 'active' para o fluxo automático funcionar
    if (this.activeCampaign.status !== 'active') {
      campaignService.updateCampaignStatus(this.activeCampaign.id, 'active');
      this.syncCampaign();
    }
    
    const nhName = this.currentStep.regionName;
    const isWholeCity = this.currentStep.regionId.startsWith('city-') || nhName.toLowerCase().includes('cidade toda');
    
    // Obter todas as palavras-chave estratégicas mapeadas para este alvo
    const rawKeywords = getTargetKeywords({ id: this.currentStep.targetId, name: this.currentStep.targetName, type: 'Serviço' });
    const targetKeywords = rawKeywords.length > 0 ? rawKeywords : [this.currentStep.targetName];
    
    let queries: string[] = [];
    let city = '';
    
    if (isWholeCity) {
      queries = targetKeywords.map(kw => `${kw} em ${this.currentStep!.cityName}, Brasil`);
      city = this.currentStep.cityName;
    } else {
      const nhWithPrefix = nhName.toLowerCase().includes('bairro') ? nhName : `Bairro ${nhName}`;
      queries = targetKeywords.map(kw => `${kw} no ${nhWithPrefix} em ${this.currentStep!.cityName}, Brasil`);
      city = `${nhWithPrefix}, ${this.currentStep.cityName}`;
    }
    
    const modes = this.activeCampaign.scanModes || ['standard'];
    const mode = modes[this.currentModeIndex] || 'standard';
    
    this.selectedChatTab = ChatTab.LEADS;
    const location = (this.currentStep.lat && this.currentStep.lng) ? { lat: this.currentStep.lat, lng: this.currentStep.lng } : undefined;
    
    // Iniciar varredura multi-termo do bairro do zero (com deduplicação atômica no banco)
    await this.deepScanManager.performMultiKeywordScan(
      queries, 
      city, 
      mode, 
      [], 
      location, 
      undefined,
      (kw, index, total) => {
        const cleanKw = targetKeywords[index - 1] || kw;
        this.scanStatus = `📍 ${nhName}: Buscando termo ${index}/${total} ("${cleanKw}")...`;
        this.requestUpdate();
      }
    );
  }

  private pauseCampaign() {
    if (this.activeCampaign) {
      campaignService.updateCampaignStatus(this.activeCampaign.id, 'paused');
      this.syncCampaign();
    }
  }

  /**
   * Mostra no proprio container do mapa por que ele nao carregou.
   * Antes, qualquer falha caia no overlay generico do Google
   * ("Ops! Algo deu errado") e a causa so aparecia no DevTools.
   */
  private mostrarErroDoMapa(titulo: string, passos: string[]) {
    if (!this.mapContainer) return;
    this.mapContainer.innerHTML = `
      <div style="height:100%;display:flex;align-items:center;justify-content:center;padding:32px;
                  font:14px/1.6 system-ui,-apple-system,'Segoe UI',sans-serif;color:#1f2937;background:#f8fafc">
        <div style="max-width:620px">
          <div style="font-size:16px;font-weight:600;margin-bottom:12px">${titulo}</div>
          <ol style="margin:0;padding-left:20px">
            ${passos.map((t) => `<li style="margin-bottom:8px">${t}</li>`).join('')}
          </ol>
        </div>
      </div>`;
  }

  async init() {
    this.savedLeads = leadService.getLeads();

    // Sem fallback: antes havia uma chave AIzaSy... literal aqui, que ia para o
    // bundle entregue ao navegador e ja esta comprometida (B-01/S-4 do NYROH.md).
    /**
     * O nome certo e `VITE_GOOGLE_MAPS_API_KEY`: e o que esta no `.env.example`,
     * no `scripts/gerar-env.cjs` e no `docs/DEPLOY.md`. Este arquivo lia
     * `VITE_MAPS_API_KEY` — um nome que nao aparece em lugar nenhum da
     * configuracao. Quem seguisse a documentacao ao pe da letra subia o painel
     * com o mapa DESLIGADO e uma mensagem pedindo uma variavel inexistente.
     *
     * O nome antigo continua aceito para nao quebrar `.env.local` ja preenchido.
     */
    const env = import.meta.env as Record<string, string | undefined>;
    const MAPS_API_KEY = (env.VITE_GOOGLE_MAPS_API_KEY ?? env.VITE_MAPS_API_KEY ?? '').trim();

    if (!MAPS_API_KEY) {
      // O mapa e uma aba entre seis. Abas Leads/Campanhas/Central JSON funcionam
      // sem Maps, entao aqui a falha e mostrada em vez de derrubar a aplicacao.
      this.mostrarErroDoMapa('O mapa esta desligado: falta VITE_GOOGLE_MAPS_API_KEY.', [
        'Copie <code>.env.example</code> para <code>.env.local</code>.',
        'Preencha <code>VITE_GOOGLE_MAPS_API_KEY</code> com uma chave do Google Cloud.',
        'Na chave, habilite <b>Maps JavaScript API</b> e <b>Places API</b>.',
        'Reinicie o servidor de desenvolvimento.',
      ]);
      this.syncCampaign();
      return;
    }

    // O Google reporta falha de autenticacao por este callback global, nao por
    // excecao — sem ele o unico sinal e uma linha de ERROR no console.
    (window as any).gm_authFailure = () => {
      this.mostrarErroDoMapa('O Google recusou a chave do Maps.', [
        'Erro mais provavel: <b>RefererNotAllowedMapError</b> — a chave tem restricao de referrer.',
        `Autorize <code>${window.location.origin}/*</code> em Credenciais &rarr; a chave &rarr; Restricoes de aplicativo &rarr; Sites.`,
        'Confirme que <b>Maps JavaScript API</b> e <b>Places API</b> estao habilitadas no projeto.',
        'Confirme que o projeto tem faturamento ativo.',
        'A mensagem exata esta no console do navegador (F12).',
      ]);
    };

    await this.mapsService.init(this.mapContainer, MAPS_API_KEY);
    this.syncCampaign();
  }

  createRenderRoot() {
    return this;
  }

  setChatState(state: ChatState) {
    this.chatState = state;
  }

  async renderMapQuery(params: any) {
    const google = this.mapsService.google;
    if (!google) {
      setTimeout(() => this.renderMapQuery(params), 500);
      return;
    }

    this.deepScanManager.clearMap();
    this.mapsService.directionsRenderer.setDirections({ routes: [] });

    if (params.deepScan) {
      this.selectedChatTab = ChatTab.LEADS;
      // Stop ongoing enrichment when starting a new scan
      this.enrichmentQueue = [];
      this.isEnriching = false;
      this.dispatchEnrichmentStatus();
      
      await this.deepScanManager.performScan(
        params.deepScan.query, 
        params.deepScan.city, 
        params.deepScan.mode, 
        this.savedLeads, 
        params.deepScan.location
      );
    } else if (params.origin && params.destination) {
      this.mapsService.directionsService.route({
        origin: params.origin,
        destination: params.destination,
        travelMode: google.maps.TravelMode.DRIVING,
      }, (response: any, status: any) => {
        if (status === 'OK') this.mapsService.directionsRenderer.setDirections(response);
      });
    } else if (params.search || params.location) {
      const query = params.search || params.location;
      const { results } = await this.mapsService.textSearch(query);
      const bounds = new google.maps.LatLngBounds();
      for (const res of results) {
        const marker = this.mapsService.addMarker({
          position: res.geometry.location,
          title: res.name
        });
        marker.addListener('click', () => this.openLeadDetails(res));
        bounds.extend(res.geometry.location);
      }
      if (results.length > 0) this.mapsService.map.fitBounds(bounds);
    }
  }

  async openLeadDetails(place: any, quiet = false) {
    try {
      // Always fetch details to maintain the "Rich" UI requirement
      const details = await this.mapsService.getDetails(place.place_id, [
        'name', 'rating', 'formatted_phone_number', 'website', 'reviews', 'geometry', 
        'formatted_address', 'user_ratings_total', 'url', 'editorial_summary', 
        'opening_hours', 'types', 'photos', 'vicinity'
      ]);
      
      this.selectedPlace = details;
      details.isEnriched = true;
      details.enriched_at = Date.now();
      
      // Converte fotos para Base64 para persistência eterna (não expirar link)
      if (details.photos && details.photos.length > 0) {
        const { convertImageToBase64 } = await import('./src/utils/image-utils');
        
        let newApiPhotoUrls: string[] = [];
        try {
           const placeInstance = new this.mapsService.google.maps.places.Place({ id: place.place_id });
           await placeInstance.fetchFields({ fields: ['photos'] });
           if (placeInstance.photos) {
              newApiPhotoUrls = placeInstance.photos.map((ph: any) => {
                 return typeof ph.getURI === 'function' ? ph.getURI({ maxHeight: 800 }) : '';
              });
           }
        } catch (err) {
           console.warn('Failed to fetch from new Place SDK API', err);
        }

        // Vamos converter até 10 fotos para não sobrecarregar demais, mas cobrir o que o usuário vê
        const photosToConvert = details.photos.slice(0, 10);
        const photoPromises = photosToConvert.map(async (p: any, i: number) => {
          try {
            let url = newApiPhotoUrls[i] || (typeof p.getUrl === 'function' ? p.getUrl({ maxWidth: 800 }) : (p.url || ''));

            if (!url || url.startsWith('data:')) return p; // Já convertido ou inválido
            
            const base64 = await convertImageToBase64(url);
            return { 
              ...p,
              url: base64, 
              html_attributions: p.html_attributions || [],
              height: p.height,
              width: p.width
            };
          } catch (err) {
            console.warn('Falha ao converter foto:', err);
            return p;
          }
        });
        
        // Substituindo o array original pelos convertidos
        details.photos = await Promise.all(photoPromises);
      }
      
      // Update savedLeads to include rich details
      const index = this.savedLeads.findIndex(l => l.place_id === place.place_id);
      if (index !== -1) {
        // Merge details but keep existing local fields like scannedAt
        const updatedLead = { ...this.savedLeads[index], ...details };
        this.savedLeads[index] = updatedLead;
        this.savedLeads = [...this.savedLeads]; // Trigger reactivity
        
        // Ensure the enriched data is persisted globally
        leadService.updateLead(updatedLead);
        
        // Ensure the event is dispatched to update any other listeners
        window.dispatchEvent(new CustomEvent('leads-persisted'));
        this.dispatchEvent(new CustomEvent('leads-updated', { detail: this.savedLeads }));
      }
    } catch (e) {
      this.selectedPlace = { ...place, fallback: true };
    }
    
    if (!quiet) {
      this.isDrawerOpen = true;
      if (this.selectedPlace.geometry?.location) {
        this.mapsService.map.panTo(this.selectedPlace.geometry.location);
      }
    }
  }

  public startEnrichment(specificPlaceIds?: string[]) {
    console.log('startEnrichment called. Processing state:', this.isProcessingEnrichment, 'Specific IDs:', specificPlaceIds?.length);
    if (this.isProcessingEnrichment) return;
    
    // Se específicos foram passados, usamos eles como a fila diretamente (permite re-enriquecer)
    if (specificPlaceIds && specificPlaceIds.length > 0) {
      this.enrichmentQueue = this.savedLeads.filter(l => specificPlaceIds.includes(l.place_id));
    } else {
      // Se não, pegamos do contexto atual e filtramos pelo que REALMENTE precisa
      let pool = this.getLeadsForCurrentContext(); 
      this.enrichmentQueue = this.leadsRequiringEnrichment(pool);
    }

    this.enrichmentTotal = this.enrichmentQueue.length;

    console.log('Enrichment queue size:', this.enrichmentQueue.length);

    if (this.enrichmentQueue.length > 0) {
      this.isEnriching = true;
      this.enrichedCount = 0;
      this.dispatchEnrichmentStatus();
      this.processEnrichmentQueue();
    } else {
      alert('Os leads selecionados já possuem todos os dados (fotos e contatos) ou não podem ser enriquecidos.');
      console.log('All selected leads already enriched.');
    }
  }

  private leadsRequiringEnrichment(pool: any[]): any[] {
     return pool.filter(l => {
        // Se já foi marcado como enriquecido e tem o mínimo, pulamos
        // Rigoroso: Se não tem pelo menos 3 fotos ou falta contato, re-enriquece
        if (l.isEnriched && (l.photos?.length >= 3 && l.formatted_phone_number)) return false;
        
        const hasEnoughPhotos = l.photos && l.photos.length >= 3;
        const hasContact = !!l.formatted_phone_number || !!l.website;
        
        return !l.isEnriched || !hasEnoughPhotos || !hasContact;
     });
  }

  private async processEnrichmentQueue() {
    if (this.isProcessingEnrichment || this.enrichmentQueue.length === 0) {
      this.isProcessingEnrichment = false;
      this.isEnriching = false;
      return;
    }
    
    this.isProcessingEnrichment = true;
    console.log(`Starting high-speed safe parallel enrichment (${this.enrichmentQueue.length} leads in queue)...`);

    // Pool de 5 workers concorrentes com micro-escalonamento seguro
    const CONCURRENCY = 5;

    const worker = async () => {
      while (this.enrichmentQueue.length > 0) {
        const lead = this.enrichmentQueue.shift();
        if (!lead) continue;

        try {
          // Micro-intervalo seguro (40ms) entre requisições
          await new Promise(resolve => setTimeout(resolve, 40));
          
          // Enriquecer silenciosamente sem travar interface
          await this.openLeadDetails(lead, true);
        } catch (e) {
          console.warn('First attempt enrichment failed for', lead.name, 'retrying once...');
          try {
            await new Promise(resolve => setTimeout(resolve, 300));
            await this.openLeadDetails(lead, true);
          } catch (retryErr) {
            console.error('Enrichment retry failed for', lead.name, retryErr);
          }
        } finally {
          this.enrichedCount++;
          this.dispatchEnrichmentStatus();
        }
      }
    };

    // Inicia os workers com micro-defasagem suave de 50ms
    const workers = Array.from({ length: CONCURRENCY }, async (_, i) => {
      if (i > 0) {
        await new Promise(r => setTimeout(r, i * 50));
      }
      return worker();
    });

    await Promise.all(workers);

    console.log('Enrichment queue complete.');
    this.isEnriching = false;
    this.isProcessingEnrichment = false;
    this.dispatchEnrichmentStatus();
  }

  private dispatchEnrichmentStatus() {
    this.dispatchEvent(new CustomEvent('enrichment-updated', {
      detail: {
        isEnriching: this.isEnriching,
        enrichedCount: this.enrichedCount,
        totalCount: this.enrichmentTotal,
        isWhatsAppEnriching: this.isWhatsAppEnriching,
        whatsappEnrichedCount: this.whatsappEnrichedCount,
        whatsappTotal: this.whatsappTotal,
        lastWhatsAppError: this.lastWhatsAppError
      }
    }));
  }

  public startWhatsAppBatch(specificPlaceIds?: string[]) {
    if (this.isProcessingWhatsApp) return;
    
    // Fila apenas de leads que ainda não tem dados de whatsapp e tem telefone
    let pool = this.savedLeads;
    
    if (specificPlaceIds && specificPlaceIds.length > 0) {
      // Se selecionou manual, pegamos esses
      this.whatsappQueue = this.savedLeads.filter(l => specificPlaceIds.includes(l.place_id) && l.formatted_phone_number);
    } else {
      // Se não, pegamos do contexto atual filtrando quem realmente precisa (para não oscilar o total)
      pool = this.getLeadsForCurrentContext();
      this.whatsappQueue = pool.filter(l => l.formatted_phone_number && (!l.whatsapp_status || l.whatsapp_status === 'pending'));
    }

    this.whatsappTotal = this.whatsappQueue.length;
    
    if (this.whatsappQueue.length > 0) {
      this.isWhatsAppEnriching = true;
      this.whatsappEnrichedCount = 0;
      this.lastWhatsAppError = null;
      this.dispatchEnrichmentStatus();
      this.processWhatsAppQueue();
    } else {
      alert('Nenhum dos leads selecionados requer verificação ou possui telefone.');
    }
  }

  private async processWhatsAppQueue() {
    if (this.isProcessingWhatsApp || this.whatsappQueue.length === 0) {
      this.isProcessingWhatsApp = false;
      this.isWhatsAppEnriching = false;
      this.dispatchEnrichmentStatus();
      return;
    }

    this.isProcessingWhatsApp = true;
    console.log(`Iniciando validação de WhatsApp segura e otimizada (${this.whatsappQueue.length} leads na fila)...`);
    
    // Concorrência ultra-segura (2 workers) com cadência controlada para evitar bloqueios no WhatsApp
    const WA_CONCURRENCY = 2;

    const worker = async () => {
      while (this.whatsappQueue.length > 0) {
        const lead = this.whatsappQueue.shift();
        if (!lead) continue;

        try {
          // Micro-intervalo seguro (400ms) entre consultas para respeitar os limites do chip
          await new Promise(r => setTimeout(r, 400));

          // Enriquecer whatsapp (fetch & base64 conversion)
          const whatsappData = await evolutionService.fetchWhatsAppProfile(lead.formatted_phone_number);
          const whatsapp_status = whatsappData.verified ? (whatsappData.exists ? 'exists' : 'not_exists') : 'pending';
          const updatedLead = { ...lead, whatsappData, whatsapp_status };
          
          // Update local state
          const index = this.savedLeads.findIndex(l => l.place_id === lead.place_id);
          if (index !== -1) {
            this.savedLeads[index] = updatedLead;
            this.savedLeads = [...this.savedLeads];
            if (this.selectedPlace?.place_id === lead.place_id) {
              this.selectedPlace = updatedLead;
            }
          }
          
          // Persist
          await leadService.updateLead(updatedLead);
          this.dispatchEvent(new CustomEvent('leads-updated', { detail: this.savedLeads }));
        } catch (err: any) {
          console.error('Batch WhatsApp error for lead:', lead.name, err);
          this.lastWhatsAppError = `${lead.name}: ${err.message || 'Erro desconhecido'}`;
          this.dispatchEnrichmentStatus();
          
          // Intervalo de respiro no caso de erro na instância
          await new Promise(r => setTimeout(r, 800));
        } finally {
          this.whatsappEnrichedCount++;
          this.dispatchEnrichmentStatus();
        }
      }
    };

    // Inicia os 2 workers com defasagem suave de 300ms
    const workers = Array.from({ length: WA_CONCURRENCY }, async (_, i) => {
      if (i > 0) {
        await new Promise(r => setTimeout(r, i * 300));
      }
      return worker();
    });

    await Promise.all(workers);

    this.isWhatsAppEnriching = false;
    this.isProcessingWhatsApp = false;
    this.dispatchEnrichmentStatus();
  }

  addMessage(role: string, message: string) {
    const div = document.createElement('div');
    div.className = `turn role-${role.trim()}`;
    
    // Structure as expected by index.ts
    const details = document.createElement('details');
    details.className = 'thinking hidden';
    details.open = true;
    details.innerHTML = '<summary>Thinking...</summary><div></div>';
    
    const text = document.createElement('div');
    text.className = 'text';
    text.textContent = message;
    
    div.append(details, text);
    this.messages = [...this.messages, div];
    
    return { thinking: details.querySelector('div')!, text };
  }

  private handleSendMessage(e: CustomEvent) {
    if (this.sendMessageHandler) {
      this.addMessage('user', e.detail);
      this.sendMessageHandler(e.detail, 'user');
    }
  }

  render() {
    return html`
      <div class="playground" style="height: 100%; display: flex; flex-direction: row; overflow: hidden;">
        <div class="sidebar">
          <div class="selector">
            <button class=${classMap({ active: this.selectedChatTab === ChatTab.GEMINI })} @click=${() => this.selectedChatTab = ChatTab.GEMINI}>Assistente</button>
            <button class=${classMap({ active: this.selectedChatTab === ChatTab.LEADS })} @click=${() => this.selectedChatTab = ChatTab.LEADS}>Leads DB (${this.savedLeads.length})</button>
          </div>
          
          <chat-view 
            class=${classMap({ hidden: this.selectedChatTab !== ChatTab.GEMINI })}
            .messages=${this.messages}
            .chatState=${this.chatState}
            @send-message=${this.handleSendMessage}
          ></chat-view>

          <leads-list
            class=${classMap({ hidden: this.selectedChatTab !== ChatTab.LEADS })}
            .leads=${this.savedLeads}
            .isScanning=${this.isScanning}
            .scanProgress=${this.scanProgress}
            .scanStatus=${this.scanStatus}
            .isEnriching=${this.isEnriching}
            .enrichedCount=${this.enrichedCount}
            .isWhatsAppEnriching=${this.isWhatsAppEnriching}
            .whatsappEnrichedCount=${this.whatsappEnrichedCount}
            .totalCount=${this.savedLeads.length}
            @start-scan=${(e: CustomEvent) => this.renderMapQuery({ deepScan: e.detail })}
            @stop-scan=${() => this.deepScanManager.stop()}
            @lead-selected=${(e: CustomEvent) => this.openLeadDetails(e.detail)}
            @start-enrichment=${() => this.startEnrichment()}
          ></leads-list>
        </div>

        <div class="main-container" style="flex: 1; display: flex; flex-direction: column; position: relative;">
          ${this.renderCampaignBanner()}
          <div style="flex: 1; position: relative; overflow: hidden;">
            ${this.mapContainer}

            <!-- Map Controls -->
            <div style="position: absolute; top: 20px; right: 20px; display: flex; flex-direction: column; gap: 10px; z-index: 10;">
              <button 
                @click=${this.toggleSelectionMode}
                class="map-control-btn ${this.isSelectionMode ? 'active' : ''}"
                style="background: white; border: none; padding: 10px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); cursor: pointer; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; color: ${this.isSelectionMode ? '#1a73e8' : '#666'};"
                title="Desenhar área de busca"
              >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24" fill="currentColor">
                  <path d="M120-120v-720h720v720H120Zm80-80h560v-560H200v560Zm0 0v-560 560Z"/>
                </svg>
              </button>
            </div>

            <!-- Selection Overlay -->
            ${this.isSelectionMode ? html`
              <div style="position: absolute; top: 20px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.8); color: white; padding: 10px 20px; border-radius: 30px; font-size: 14px; z-index: 100; pointer-events: none;">
                Clique e arraste no mapa para selecionar a área
              </div>
            ` : ''}

            <!-- Manual Selection Action -->
            ${this.manualBounds ? html`
               <div style="position: absolute; bottom: 30px; left: 50%; transform: translateX(-50%); background: white; padding: 15px 25px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 100; display: flex; gap: 15px; align-items: center; width: 450px;">
                 <div style="flex: 1;">
                   <div style="font-weight: bold; color: #333; font-size: 14px; margin-bottom: 8px;">Área Selecionada</div>
                   <input 
                     type="text" 
                     placeholder="O que buscar? (Ex: Restaurantes)" 
                     .value=${this.manualQuery}
                     @input=${(e: any) => this.manualQuery = e.target.value}
                     @keydown=${(e: any) => e.key === 'Enter' && this.scanManualArea()}
                     style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;"
                   />
                 </div>
                 <div style="display: flex; flex-direction: column; gap: 8px;">
                   <button @click=${this.scanManualArea} style="background: #1a73e8; color: white; border: none; padding: 8px 15px; border-radius: 6px; cursor: pointer; font-weight: bold; font-size: 13px;">Escanear</button>
                   <button @click=${this.clearManualSelection} style="background: #eee; color: #666; border: none; padding: 6px 15px; border-radius: 6px; cursor: pointer; font-size: 12px;">Cancelar</button>
                 </div>
               </div>
            ` : ''}

            <div class="drawer-overlay ${this.isDrawerOpen ? 'open' : ''}" @click=${() => this.isDrawerOpen = false}></div>
            <div class="right-drawer ${this.isDrawerOpen ? 'open' : ''}">
              <lead-drawer .place=${this.selectedPlace} .isOpen=${this.isDrawerOpen} @close-drawer=${() => this.isDrawerOpen = false}></lead-drawer>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderCampaignBanner() {
    if (!this.activeCampaign || !this.currentStep) return '';

    return html`
      <div class="campaign-banner" style="background: #1a73e8; color: white; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 5px rgba(0,0,0,0.2); z-index: 100;">
        <div style="display: flex; align-items: center; gap: 15px;">
          <div class="${this.isScanning ? 'rotating' : ''}" style="font-size: 20px;">${this.isScanning ? '⚙️' : '⚡'}</div>
          <div>
            <div style="font-size: 14px; font-weight: bold;">Campanha Ativa: ${this.activeCampaign.name}</div>
            <div style="font-size: 12px; opacity: 0.9;">Etapa: "${this.currentStep.targetName}" em ${this.currentStep.regionName} (${this.currentStep.cityName})</div>
            ${this.activeCampaign.scanModes && this.activeCampaign.scanModes.length > 1 ? html`
              <div style="font-size: 10px; background: rgba(0,0,0,0.2); padding: 2px 8px; border-radius: 10px; margin-top: 4px; display: inline-block;">
                Ciclo: ${this.currentModeIndex + 1} de ${this.activeCampaign.scanModes.length} (${this.activeCampaign.scanModes[this.currentModeIndex]})
              </div>
            ` : ''}
          </div>
        </div>
        <div style="display: flex; gap: 10px;">
          <button @click=${this.pauseCampaign} style="background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.4); color: white; padding: 6px 12px; border-radius: 4px; font-size: 13px; cursor: pointer;">
            Pausar
          </button>
          <button @click=${this.runNextCampaignStep} ?disabled=${this.isScanning} style="background: white; border: none; color: #1a73e8; padding: 6px 15px; border-radius: 4px; font-size: 13px; font-weight: bold; cursor: pointer; opacity: ${this.isScanning ? 0.5 : 1};">
            ${this.isScanning ? 'Processando...' : 'Iniciar Extração'}
          </button>
        </div>
      </div>
    `;
  }

  scrollToTheEnd() {
    this.renderRoot.querySelector('chat-view')?.scrollToBottom();
  }

  setInputField(msg: string) {
    this.renderRoot.querySelector('chat-view')?.setInputValue(msg);
  }
}
